import React, { FC, useCallback, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { metaAI } from "neql"
import {
    AIModel,
    AITrainingInstance,
    AITrainingTemplate as AITrainingTemplateType,
    shouldStopRefreshingAIInstance
} from "../../../../../../../Models/API/AIModel"
import { AppDetailBase } from "../../AppDetailBase/AppDetailBase"
import { useNeqlContextProvider } from "../../../../../../../Hooks/useNeqlContextProvider"
import { MainThunkDispatch } from "../../../../../../../Reducers"
import { EmptyTrainingView } from "./Common/EmptyTrainingView"
import {
    failMessage,
    successMessage
} from "../../../../../../../Actions/Client/MessagesActions"
import { Loader, Button } from "dash-styled"
import { AddAITrainingTemplateModal } from "./Common/AddAITrainingTemplateModal"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { useAssumedRoles } from "../../../../../../../Hooks/useAssumedRoles"
import {
    getValue,
    hasValue
} from "../../../../../../../Models/Client/FetchState"
import { useInterval } from "../../../../../../../Hooks/useInterval"
import { useAIModels } from "../../../../../../../Hooks/useAIModels"
import { AITrainingTemplate } from "./Common/AITrainingTemplate"
import { AddAITrainingInstanceModal } from "./Common/AddAITrainingInstanceModal"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../../Routes"
import { useAIModelsForApp } from "../../../../../../../Hooks/useAIModelsForApp"
import { getTrainingColumnDefinitions } from "./Common/TrainingTableColumns"

export interface AITrainingListProps {
    orgUsername: string
    appID: string
}

/**
 * No longer used, kept for future reuse
 */
export const AITrainingList: FC<AITrainingListProps> = ({
    orgUsername,
    appID
}) => {
    const [isTemplateModalShown, showTemplateModal, hideTemplateModal] =
        useBoolean(false)
    const [selectedTrainingTemplate, setSelectedTrainingTemplate] =
        useState<AITrainingTemplateType | undefined>()

    const resetTrainingTemplate = useCallback(
        () => setSelectedTrainingTemplate(undefined),
        []
    )
    const dispatch = useDispatch<MainThunkDispatch>()
    const clientForApp = useNeqlContextProvider("metaAIApp", appID)

    const rolesFetch = useAssumedRoles(appID)
    const appRoles = useMemo(() => getValue(rolesFetch) ?? [], [rolesFetch])
    const { data: availableAITrainingModelsForTemplate } = useAIModels(
        {
            trainable: true
        },
        appID
    )
    const { data: availableAITrainingModelsForInstance } = useAIModels(
        {
            rootId: selectedTrainingTemplate?.modelId,
            trainable: true
        },
        appID
    )
    const { data: trainingTemplateList, refetch: refetchTrainingTemplate } =
        metaAI.useGetAiTrainingTemplateQuery({
            client: clientForApp,
            variables: {
                offset: 0,
                limit: 50,
                where: { appId: { _eq: appID } }
            }
        })

    const [createAIDataset, { loading: isCreatingDataset }] =
        metaAI.useInsertMetaAiDatasetMutation({ client: clientForApp })

    const [
        createAITrainingTemplateForApp,
        { loading: isCreatingAITrainingForApp }
    ] = metaAI.useInsertMetaAiTrainingTemplateMutation({
        client: clientForApp
    })
    const [
        createAITrainingInstanceForApp,
        { loading: isCreatingAITrainingInstanceForApp }
    ] = metaAI.useInsertMetaAiTrainingInstanceMutation({ client: clientForApp })

    const { data: modelList, refetch: refetchModels } = useAIModelsForApp(appID)
    const modelInUseMap = useMemo<Map<string, true>>(
        () =>
            modelList?.meta_ai_model === undefined
                ? new Map<string, true>()
                : new Map(modelList.meta_ai_model.map(m => [m.id, true])),
        [modelList?.meta_ai_model]
    )
    const handleUsedModelClick = useCallback(
        (modelID: string) =>
            dispatch(
                push(routePaths.appModelDetail(orgUsername, appID, modelID))
            ),
        [appID, dispatch, orgUsername]
    )

    const handleModelMetricsClick = useCallback(
        (datasetID: string) =>
            dispatch(
                push(
                    routePaths.appModelsComparison(
                        orgUsername,
                        appID,
                        datasetID
                    )
                )
            ),
        [orgUsername, appID, dispatch]
    )

    const instanceColumns = useMemo(
        () =>
            getTrainingColumnDefinitions(
                modelInUseMap,
                handleUsedModelClick,
                handleModelMetricsClick
            ),
        [modelInUseMap, handleUsedModelClick, handleModelMetricsClick]
    )
    const handleClickRow = useCallback(
        ({ id }: AITrainingInstance) =>
            dispatch(
                push(
                    routePaths.appTrainingInstanceDetail(orgUsername, appID, id)
                )
            ),
        [appID, dispatch, orgUsername]
    )
    const handleTrainingTemplateCreate = useCallback(
        async (model: AIModel) => {
            try {
                await createAITrainingTemplateForApp({
                    variables: {
                        object: {
                            appId: appID,
                            modelId: model.root_id,
                            name: `${model.name}-template`
                        }
                    }
                })

                refetchTrainingTemplate()
                hideTemplateModal()
                dispatch(successMessage("AI training template is added"))
            } catch (e) {
                dispatch(
                    failMessage("Could not create AI training template", e)
                )
            }
        },
        [
            appID,
            createAITrainingTemplateForApp,
            dispatch,
            refetchTrainingTemplate,
            hideTemplateModal
        ]
    )

    const handleTrainingInstanceCreate = useMemo(() => {
        if (selectedTrainingTemplate === undefined) {
            return
        }
        return async (
            modelId: string,
            properties: unknown,
            taskName: string
        ) => {
            try {
                const dataset = await createAIDataset({
                    variables: {
                        object: { task_name: taskName }
                    }
                })
                if (
                    dataset.data?.insert_meta_ai_dataset_one?.id === undefined
                ) {
                    dispatch(failMessage("Could not create AI training"))
                    return
                }

                await createAITrainingInstanceForApp({
                    variables: {
                        objects: {
                            trainingTemplateId: selectedTrainingTemplate.id,
                            ...(properties !== undefined && properties !== null
                                ? { currentProperties: properties }
                                : {}),
                            dataset_id:
                                dataset.data.insert_meta_ai_dataset_one.id,
                            modelId
                        }
                    }
                })
                refetchTrainingTemplate()
                resetTrainingTemplate()
                refetchModels()
                dispatch(successMessage("AI training template is added"))
            } catch (e) {
                dispatch(failMessage("Could not create AI training", e))
            }
        }
    }, [
        createAIDataset,
        createAITrainingInstanceForApp,
        dispatch,
        refetchTrainingTemplate,
        resetTrainingTemplate,
        selectedTrainingTemplate,
        refetchModels
    ])

    const trainingInstances = useMemo<AITrainingInstance[]>(
        () =>
            trainingTemplateList?.meta_ai_training_template
                .flatMap(
                    trainingTemplate => trainingTemplate.training_instances
                )
                .filter(trainingInstance => trainingInstance !== undefined) ??
            [],
        [trainingTemplateList]
    )
    const shouldStopRefreshing = useMemo(
        () => trainingInstances.every(shouldStopRefreshingAIInstance),
        [trainingInstances]
    )

    useInterval(refetchTrainingTemplate, 5000, shouldStopRefreshing)

    useInterval(refetchModels, 5000, shouldStopRefreshing)

    const totalItemCount =
        trainingTemplateList?.meta_ai_training_template.length

    const modelsForTemplate = useMemo(
        () =>
            (availableAITrainingModelsForTemplate?.meta_ai_model ?? [])
                .filter(
                    (model, index, modelList) =>
                        modelList.findIndex(
                            m => m.root_id === model.root_id
                        ) === index
                )
                .filter(model =>
                    trainingTemplateList?.meta_ai_training_template.every(
                        trainingTemplate =>
                            trainingTemplate.modelId !== model.root_id
                    )
                ),
        [
            availableAITrainingModelsForTemplate?.meta_ai_model,
            trainingTemplateList?.meta_ai_training_template
        ]
    )

    const modelVersionsToTrain = useMemo(
        () => availableAITrainingModelsForInstance?.meta_ai_model ?? [],
        [availableAITrainingModelsForInstance?.meta_ai_model]
    )

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title="AI Training"
            breadcrumbs={[]}
            fixedContentWidth={false}
        >
            {totalItemCount === undefined ||
            availableAITrainingModelsForTemplate === undefined ||
            !hasValue(rolesFetch) ? (
                <Loader />
            ) : totalItemCount === 0 ? (
                <EmptyTrainingView
                    appID={appID}
                    onAdd={
                        availableAITrainingModelsForTemplate.meta_ai_model
                            .length > 0
                            ? showTemplateModal
                            : undefined
                    }
                />
            ) : (
                <>
                    <Button
                        className="w-48"
                        onClick={showTemplateModal}
                        disabled={modelsForTemplate.length === 0}
                    >
                        Add AI Template
                    </Button>
                    <div className="mt-4 divide-y divide-rhino-300 ">
                        {trainingTemplateList?.meta_ai_training_template.map(
                            trainingTemplate => (
                                <AITrainingTemplate
                                    key={trainingTemplate.id}
                                    aiTrainingTemplate={trainingTemplate}
                                    onTrain={setSelectedTrainingTemplate}
                                    onAIInstanceClick={handleClickRow}
                                    columns={instanceColumns}
                                    appRoles={appRoles}
                                    appID={appID}
                                />
                            )
                        )}
                    </div>
                </>
            )}
            {isTemplateModalShown && (
                <AddAITrainingTemplateModal
                    modelChoices={modelsForTemplate}
                    isProcessing={isCreatingAITrainingForApp}
                    onSubmit={handleTrainingTemplateCreate}
                    onClose={hideTemplateModal}
                />
            )}

            {selectedTrainingTemplate && (
                <AddAITrainingInstanceModal
                    appID={appID}
                    selectedTrainingTemplate={selectedTrainingTemplate}
                    modelChoices={modelVersionsToTrain}
                    isProcessing={
                        isCreatingAITrainingForApp ||
                        isCreatingAITrainingInstanceForApp ||
                        isCreatingDataset
                    }
                    onSubmit={handleTrainingInstanceCreate}
                    onClose={resetTrainingTemplate}
                />
            )}
        </AppDetailBase>
    )
}
